/**
* @Author: Michael Neumair <mBook>
* @Date:   2016-08-22T13:39:18+02:00
* @Email:  7q7w7e7r@gmail.com
* @Last modified by:   mBook
* @Last modified time: 2016-08-31T14:29:43+02:00
*/


import warning from 'warning';
import moment from 'moment';

const dayAbbreviation = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const dayList = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const monthList = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep',
  'Oct', 'Nov', 'Dec'];
const monthLongList = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];


/**
 * formatting a Date according to locale and options parameters
 *
 * @param {string} locale
 * @param {object} options
 * @return {undefined}
 */
export function dateTimeFormat(locale, options) {
  warning(locale === 'en-US', `The ${locale} locale is not supported by the built-in DateTimeFormat.
    Use the \`DateTimeFormat\` prop to supply an alternative implementation.`);

  this.format = function(date) {
    if (options.month === 'short' && options.weekday === 'short' && options.day === '2-digit') {
      return `${dayList[date.getDay()]}, ${monthList[date.getMonth()]} ${date.getDate()}`;
    } else if (options.day === 'numeric' && options.month === 'numeric' && options.year === 'numeric') {
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    } else if (options.month === 'long' && options.year === 'numeric') {
      return `${monthLongList[date.getMonth()]} ${date.getFullYear()}`;
    } else if (options.weekday === 'narrow') {
      return dayAbbreviation[date.getDay()];
    } else {
      warning(false, 'Wrong usage of DateTimeFormat');
    }
  };
}

/**
 * date modification: days
 *
 * @param {date} d
 * @param {number} days
 * @return {date}
 */
export function addDays(d, days) {
  const newDate = cloneDate(d);
  newDate.setDate(d.getDate() + days);
  return newDate;
}

/**
 * date modification: months
 *
 * @param {date} d
 * @param {number} months
 * @return {date}
 */
export function addMonths(d, months) {
  const newDate = cloneDate(d);
  newDate.setMonth(d.getMonth() + months);
  return newDate;
}

/**
 * date modification: years
 *
 * @param {date} d
 * @param {number} years
 * @return {date}
 */
export function addYears(d, years) {
  const newDate = cloneDate(d);
  newDate.setFullYear(d.getFullYear() + years);
  return newDate;
}

/**
 * date modification: clone
 *
 * @param {date} d
 * @return {date}
 */
export function cloneDate(d) {
  return new Date(d.getTime());
}

/**
 * date modification: clone (with time zero'ed out)
 *
 * @param {date} d
 * @return {date}
 */
export function cloneAsDate(d) {
  const clonedDate = cloneDate(d);
  clonedDate.setHours(0, 0, 0, 0);
  return clonedDate;
}

/**
 * get date parts: days (in currently set month)
 *
 * @param {date} d
 * @return {date}
 */
export function getDaysInMonth(d) {
  const resultDate = getFirstDayOfMonth(d);

  resultDate.setMonth(resultDate.getMonth() + 1);
  resultDate.setDate(resultDate.getDate() - 1);

  return resultDate.getDate();
}

/**
 * get date parts: first day (in currently set month)
 *
 * @param {date} d
 * @return {date}
 */
export function getFirstDayOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/**
 * get date parts: firstv days (in currently set week)
 *
 * @param {date} d
 * @return {date}
 */
export function getFirstDayOfWeek() {
  const now = new Date();
  return new Date(now.setDate(now.getDate() - now.getDay()));
}

/**
 * get date parts: weeks (in currently set month for all weeks)
 *
 * @param {date} d
 * @param {number} firstDayOfWeek
 * @return {array}
 */
export function getWeekArray(d, firstDayOfWeek) {
  const dayArray = [];
  const daysInMonth = getDaysInMonth(d);
  const weekArray = [];
  let week = [];

  for (let i = 1; i <= daysInMonth; i++) {
    dayArray.push(new Date(d.getFullYear(), d.getMonth(), i));
  }

  const addWeek = (week) => {
    const emptyDays = 7 - week.length;
    for (let i = 0; i < emptyDays; ++i) {
      week[weekArray.length ? 'push' : 'unshift'](null);
    }
    weekArray.push(week);
  };

  dayArray.forEach((day) => {
    if (week.length > 0 && day.getDay() === firstDayOfWeek) {
      addWeek(week);
      week = [];
    }
    week.push(day);
    if (dayArray.indexOf(day) === dayArray.length - 1) {
      addWeek(week);
    }
  });

  return weekArray;
}

/**
 * get date parts: dayname
 *
 * @param {number} day
 * @param {number} firstDayOfWeek
 * @return {string}
 */
export function localizedWeekday(day, firstDayOfWeek) {
  const firstDayDate = getFirstDayOfWeek();
  return moment(firstDayDate).day(day + firstDayOfWeek).format('ddd');
}

/**
 * Convert date to ISO 8601 (YYYY-MM-DD) date string
 * accounting for current timezone
 *
 * @param {date} date
 * @return {string}
 */
export function formatIso(date) {
  return (new Date(`${date.toDateString()} 12:00:00 +0000`)).toISOString().substring(0, 10);
}

/**
 * test two dates for equality (on date not time!)
 *
 * @param {date} d1
 * @param {date} d2
 * @return {boolean}
 */
export function isEqualDate(d1, d2) {
  return d1 && d2 &&
    (d1.getFullYear() === d2.getFullYear()) &&
    (d1.getMonth() === d2.getMonth()) &&
    (d1.getDate() === d2.getDate());
}

/**
 * test two dates for d1 < d2
 *
 * @param {date} d1
 * @param {date} d2
 * @return {boolean}
 */
export function isBeforeDate(d1, d2) {
  const date1 = cloneAsDate(d1);
  const date2 = cloneAsDate(d2);

  return (date1.getTime() < date2.getTime());
}

/**
 * test two dates for d1 > d2
 *
 * @param {date} d1
 * @param {date} d2
 * @return {boolean}
 */
export function isAfterDate(d1, d2) {
  const date1 = cloneAsDate(d1);
  const date2 = cloneAsDate(d2);

  return (date1.getTime() > date2.getTime());
}

/**
 * test three dates for d2 > d1 > d3
 *
 * @param {date} dateToCheck
 * @param {date} startDate
 * @param {date} endDate
 * @return {boolean}
 */
export function isBetweenDates(dateToCheck, startDate, endDate) {
  return (!(isBeforeDate(dateToCheck, startDate)) &&
    !(isAfterDate(dateToCheck, endDate)));
}

/**
 * get diff between two dates as month
 * non-fractional!
 *
 * @param {date} d1
 * @param {date} d2
 * @return {boolean}
 */
export function monthDiff(d1, d2) {
  let m;
  m = (d1.getFullYear() - d2.getFullYear()) * 12;
  m += d1.getMonth();
  m -= d2.getMonth();
  return m;
}

/**
 * get diff between two dates as year
 * non-fractional!
 *
 * @param {date} d1
 * @param {date} d2
 * @return {boolean}
 */
export function yearDiff(d1, d2) {
  return ~~(monthDiff(d1, d2) / 12);
}
